import { Empty as AntEmpty } from 'antd';
import clsx from 'clsx';
import { AntRow } from 'components/ant/AntRow';
import { Modal as UIModal, ModalProps } from 'components/UI/Modal/styled';
import React from 'react';

import s from './PreviewModal.module.less';

type EmptyProps = React.ComponentProps<typeof AntEmpty>;
type RowProps = React.ComponentProps<typeof AntRow>;

// `Modal = styled(UIModal)` → thin FC wrapping the migrated UI Modal FC
// (Plan 02-04). Forwards `className` (Pattern 3) so the scoped `.modal`
// class lands on `.ant-modal` and the `:global` `.ant-modal-body` /
// `.ant-tabs-nav-list` descendant overrides resolve. UIModal in turn
// forwards `className` + `style` onto the antd dialog root (validated in
// 02-04). Re-exported under its original public name `Modal` in styled.ts.
export const PreviewModal: React.FC<ModalProps> = (props) => {
  const { className, ...rest } = props;

  return <UIModal {...rest} className={clsx(s.modal, className)} />;
};

// `RowPreviewBig = styled(Row)` → thin FC wrapping antd `Row`; forwards
// `className` so `.rowPreviewBig` (+ `:global .anticon.anticon-warning svg`)
// resolves, while Row props (`justify`, etc.) pass through unchanged.
export const RowPreviewBig: React.FC<RowProps> = (props) => {
  const { className, children, ...rest } = props;

  return (
    <AntRow {...rest} className={clsx(s.rowPreviewBig, className)}>
      {children}
    </AntRow>
  );
};

export const Divider: React.FC<React.HTMLAttributes<HTMLDivElement>> = (props) => {
  const { className, ...rest } = props;

  return <div {...rest} className={clsx(s.divider, className)} />;
};

export const BucketName: React.FC<React.HTMLAttributes<HTMLDivElement>> = (props) => {
  const { className, children, ...rest } = props;

  return (
    <div {...rest} className={clsx(s.bucketName, className)}>
      {children}
    </div>
  );
};

export const ImgPreviewBig: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = (props) => {
  const { className, alt, ...rest } = props;

  return <img {...rest} alt={alt} className={clsx(s.imgPreviewBig, className)} />;
};

// `Empty = styled(AntEmpty)` → forwards className onto AntEmpty.
export const Empty: React.FC<EmptyProps> = (props) => {
  const { className, children, ...rest } = props;

  return (
    <AntEmpty {...rest} className={clsx(s.empty, className)}>
      {children}
    </AntEmpty>
  );
};

// Wraps `PdfViewer` so the `.pdfViewerWrapper` class applies and the viewer
// gets a resolvable height inside `.rowPreviewBig`.
export const PdfViewerWrapper: React.FC<React.HTMLAttributes<HTMLDivElement>> = (props) => {
  const { className, children, ...rest } = props;

  return (
    <div {...rest} className={clsx(s.pdfViewerWrapper, className)}>
      {children}
    </div>
  );
};

export const ScsViewerWrapper: React.FC<React.HTMLAttributes<HTMLDivElement>> = (props) => {
  const { className, children, ...rest } = props;

  return (
    <div {...rest} className={clsx(s.scsViewerWrapper, className)}>
      {children}
    </div>
  );
};

interface BucketFilenameProps extends React.HTMLAttributes<HTMLDivElement> {
  active: boolean;
}

// `active` drives the action-color text via the `.active` class (clsx).
export const BucketFilename: React.FC<BucketFilenameProps> = (props) => {
  const { active, className, children, ...rest } = props;

  return (
    <div {...rest} className={clsx(s.bucketFilename, className, { [s.active]: active })}>
      {children}
    </div>
  );
};
